import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight, Instagram, Newspaper, ExternalLink, Heart, MessageCircle, Bookmark } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { thumb, thumbWide, fallbackToFull } from "../lib/imageThumb";

// Real Instagram screenshots from @1000artsofficiel (735×1600px iPhone screenshots).
// Each image contains: iOS status bar + Instagram header + post photo (~735×735) + engagement bar.
// We use a wrapper with overflow:hidden and an absolute-positioned image at width:100%,
// then use `top` (in %) to anchor only the post photo region inside the square cell.
const INSTAGRAM_GRID = [
  { src: "/img/34d2f684c7.webp", top: "-44%" }, // Yoga group EVJF
  { src: "/img/8eff1eea80.webp", top: "-42%" }, // Racing red dancer
  { src: "/img/3043a5e62b.webp", top: "-42%" }, // Swiss Qualifications médailles
  { src: "/img/c3799ae215.webp", top: "-42%" }, // Heels duo
  { src: "/img/89192d4016.webp", top: "-42%" }, // Équipe turquoise
  { src: "/img/34d2f684c7.webp", top: "-44%" }, // Yoga (repeat to fill)
];

export default function Actualites() {
  useSEO({
    title: "Actualités et spectacles — 1000 ARTS Cugy VD",
    description: "Suivez les actualités de 1000 ARTS : spectacle annuel, portes ouvertes, événements et articles de presse. École de danse et de comédie musicale à Cugy VD.",
    canonical: "/actualites",
  });
  return (
    <div data-testid="page-actualites" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="eyebrow mb-3">Toute l'actualité</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
          Actualités<br /><span style={{ color: "var(--primary)" }}>1000 ARTS</span>
        </h1>
      </div>

      {/* INSTAGRAM — grid layout like 1000arts.ch — Cliquable partout */}
      <section
        className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24 cursor-pointer group"
        data-testid="actu-instagram"
        onClick={(e) => {
          // If user clicked a specific inner link/button, let it handle itself
          if (e.target.closest('a,button')) return;
          window.open("https://www.instagram.com/1000artsofficiel/", "_blank", "noopener,noreferrer");
        }}
        role="link"
        aria-label="Voir notre compte Instagram 1000artsofficiel"
      >
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="eyebrow mb-3" style={{ color: "var(--primary-soft)" }}>Instagram</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Sur les <span style={{ color: "var(--primary)" }}>réseaux</span></h2>
          </div>
          <a href="https://www.instagram.com/1000artsofficiel/" target="_blank" rel="noreferrer" data-testid="instagram-cta" className="btn-primary">
            <Instagram size={18} /> Voir notre Instagram
          </a>
        </div>

        <div className="card-flat transition-transform group-hover:scale-[1.005]" style={{ padding: 0, overflow: "hidden" }}>
          {/* Account header */}
          <div className="flex items-center gap-4 p-6 border-b border-[var(--border-soft)]">
            <img loading="lazy" decoding="async" src={thumb("/img/9ab2fa8bea.webp")} onError={fallbackToFull("/img/9ab2fa8bea.webp")} alt="1000 ARTS" className="w-16 h-16 rounded-full border-2 border-[var(--primary)] p-1" style={{ background: "#0F1B2D" }} />
            <div className="flex-1">
              <div className="font-display text-lg tracking-tight">1000ARTSOFFICIEL</div>
              <div className="text-xs text-[var(--muted)] mt-1">École de danse et comédie musicale 💃 Hip-Hop, chant, théâtre pour petits et grands<br/>@miller1000r @la_ptite_nono @troupe_unis_vers</div>
            </div>
            <a href="https://www.instagram.com/1000artsofficiel/" target="_blank" rel="noreferrer" className="hidden md:inline-flex px-5 py-2 rounded-lg text-sm font-medium text-white" style={{ background: "#0095F6" }}>Suivre</a>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-3 gap-1 p-1">
            {INSTAGRAM_GRID.map((item, i) => (
              <a key={i} href="https://www.instagram.com/1000artsofficiel/" target="_blank" rel="noreferrer" data-testid={`ig-${i}`} className="aspect-square overflow-hidden relative group bg-black block">
                <img loading="lazy" decoding="async" src={thumb(item.src)} onError={fallbackToFull(item.src)} alt={`Post ${i+1}`} className="absolute left-0 w-full max-w-none group-hover:scale-105 transition-transform duration-500" style={{ top: item.top, height: "auto" }} />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 text-white" style={{ background: "rgba(0,0,0,0.4)" }}>
                  <span className="flex items-center gap-1 text-sm"><Heart size={16} fill="#fff" /> ❤️</span>
                  <span className="flex items-center gap-1 text-sm"><MessageCircle size={16} /></span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* PRESS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24" data-testid="actu-press">
        <div className="eyebrow mb-3">Dans les médias</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-10">On parle de <span style={{ color: "var(--primary)" }}>nous</span></h2>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* L'ÉCHO DU GROS-DE-VAUD with Instagram-style embed */}
          <article data-testid="press-echo" className="card flex flex-col" style={{ padding: 0, overflow: "hidden" }}>
            <a href="https://www.instagram.com/p/DFqQD0EoxD3" target="_blank" rel="noreferrer" data-testid="echo-instagram-link" className="block">
              <img loading="lazy" decoding="async" src={thumbWide("/img/14e228bd26.webp")} onError={fallbackToFull("/img/14e228bd26.webp")} alt="Article L'Écho du Gros-de-Vaud sur 1000 ARTS" className="w-full h-auto" />
            </a>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <img loading="lazy" decoding="async" src={thumb("/img/a797ca003e.webp")} onError={fallbackToFull("/img/a797ca003e.webp")} alt="Logo L'Écho du Gros-de-Vaud" className="w-10 h-10 rounded-full object-cover" style={{ background: "#fff", border: "1px solid var(--border)" }} />
                <div>
                  <div className="font-display text-lg" style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}>L'Écho du Gros-de-Vaud</div>
                  <div className="text-xs text-[var(--muted)]">07.02.2025 · 04.02.2025</div>
                </div>
              </div>
              <p className="text-sm text-[var(--muted)]">« Influence new-yorkaise à l'École de danse et comédie musicale 1000 Arts » · « Une nouvelle école de danse à Cugy »</p>
              <a href="https://www.instagram.com/p/DFqQD0EoxD3" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
                Voir sur Instagram <ArrowRight size={14} />
              </a>
            </div>
          </article>

          {/* L'AGENDA — toute la section cliquable */}
          <a
            href="https://www.l-agenda.ch/1000-arts/"
            target="_blank"
            rel="noreferrer"
            data-testid="press-agenda"
            className="card flex flex-col hover:shadow-lg transition-shadow"
            style={{ padding: 0, overflow: "hidden" }}
          >
            <div className="aspect-[16/9] overflow-hidden">
              <img loading="lazy" decoding="async" src={thumbWide("/img/b87b5ea49a.webp")} onError={fallbackToFull("/img/b87b5ea49a.webp")} alt="L'Agenda Romandie — Article 1000 ARTS" className="w-full h-full object-cover" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
                  <Newspaper size={12} /> Culture romande
                </span>
                <span className="text-xs text-[var(--muted)]">27.02.2025</span>
              </div>
              <div className="font-display text-2xl mb-1">L'Agenda Romandie</div>
              <div className="font-medium text-lg mb-3">« Deux énergies solaires et 1000 Arts »</div>
              <p className="text-[var(--text)] text-sm leading-relaxed flex-1">
                Une école de danse ouvre ses portes à Cugy, à sa tête : deux amoureux, artistes idéalistes et déterminés. Noémie Vaney et Miller Sacon, la trentaine, se sont rencontrés autour de leur passion commune pour la danse et se lancent aujourd'hui dans l'aventure d'une école qui leur ressemble : ouverte, énergique, ancrée dans le hip-hop new-yorkais et enrichie par la comédie musicale. Ensemble, ils veulent bâtir un lieu où chacun peut trouver sa place, quel que soit son âge ou son niveau, et où l'art se vit comme une célébration…
                <span className="italic text-[var(--muted)]"> lire la suite</span>
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-medium mt-5" style={{ color: "var(--primary)" }}>
                Lire l'article complet <ArrowRight size={14} />
              </span>
            </div>
          </a>

          {/* REFLETS DE CUGY */}
          <a
            href="/pdf/reflets-de-cugy-1000arts.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="press-reflets"
            className="card flex flex-col hover:shadow-lg transition-shadow lg:col-span-2"
            style={{ padding: 0, overflow: "hidden" }}
          >
            <div className="grid md:grid-cols-2">
              <div className="aspect-[4/5] md:aspect-auto overflow-hidden bg-[var(--primary-tint)]">
                <img loading="lazy" decoding="async" src={thumbWide("/img/2074c5b565.webp")} onError={fallbackToFull("/img/2074c5b565.webp")} alt="Article Reflets de Cugy — 1000 ARTS, tous en scène" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
                    <Newspaper size={12} /> Journal de Cugy
                  </span>
                  <span className="text-xs text-[var(--muted)]">Octobre 2025</span>
                </div>
                <div className="font-display text-2xl mb-1">Reflets de Cugy</div>
                <div className="font-medium text-lg mb-3">« 1000 Arts : tous en scène ! »</div>
                <p className="text-[var(--text)] text-sm leading-relaxed">
                  Noémie et Miller sont des passionnés et se réjouissent de te faire découvrir leurs arts au sein de leur école nouvellement créée à Cugy : 1000 Arts. Mais qu'est-ce qui a amené ce couple de passionnés à s'installer à Cugy ? « Tout nous ramène à Cugy ». Miller Sacon, né à Villars-Tiercelin, fut éducateur dans les UAPE de Froideville et de Cugy et donnait déjà des cours de hip-hop dans notre village pour les enfants, qui rencontraient un succès certain…
                  <span className="italic text-[var(--muted)]"> lire la suite</span>
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-medium mt-5" style={{ color: "var(--primary)" }}>
                  Lire l'article complet (PDF) <ArrowRight size={14} />
                </span>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24">
        <div className="card-flat text-center" style={{ padding: "48px 32px" }}>
          <h3 className="font-display text-3xl md:text-4xl">Une question, une envie&nbsp;?</h3>
          <p className="text-[var(--muted)] mt-3">Contacte-nous ou réserve ton cours d'essai en ligne.</p>
          <div className="flex justify-center gap-3 mt-6 flex-wrap">
            <Link to="/contact" data-testid="actu-cta-contact" className="btn-outline">Nous contacter</Link>
            <Link to="/inscription" data-testid="actu-cta-inscription" className="btn-primary">S'inscrire <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
